import gql from 'graphql-tag';
import { print } from 'graphql';
import { useEffect, useRef } from 'react';
import { subscribe, type Subscription } from './lib/aggSubscription';

const realtimeChannel = gql`
	subscription realtimeChannel(
		$installationId: ID!
		$name: String!
		$context: String
		$isGlobal: Boolean
	) {
		ecosystem {
			realtimeChannel(
				installationId: $installationId
				name: $name
				context: $context
				isGlobal: $isGlobal
			) {
				payload
			}
		}
	}
`;

export type SubscriptionCallback = (payload: unknown, error?: Error) => void;

export type SubscribeRealtimeChannelPayload = {
	data: {
		channelName: string;
		isGlobal?: boolean;
		onEvent: SubscriptionCallback;
		options?: {
			replay: number;
		};
	};
};

export type SubscribeRealtimeChannel = (
	payload: SubscribeRealtimeChannelPayload,
	contextToken?: string,
) => Promise<{ unsubscribe: () => void } | undefined>;

type SubscriptionResult = {
	data: {
		ecosystem: {
			realtimeChannel?: {
				payload?: string;
			};
		};
	};
};

const getContextKey = (context: { channelName: string; isGlobal?: boolean }) => {
	return Object.entries(context)
		.sort((a, b) => a[0].localeCompare(b[0]))
		.toString();
};

const unsubscribeAllSubscriptions = (activeSubscriptions: Map<string, Subscription>) => {
	activeSubscriptions.forEach((subscription) => {
		if (!subscription.closed) {
			subscription.unsubscribe();
		}
	});
	activeSubscriptions.clear();
};

/**
 * This hook is used to subscribe to client events from the extension.
 * It will return a function that can be called to unsubscribe from the client events.
 *
 * This hook has no side effect (i.e. does not actively trigger any network calls or actively making any subscription.
 * It is because everything inside this hook is lazily initialized.
 * Importantly:
 * - It only has impact when the subscribeClientEvent is called by the client (e.g. @forge/bridge)
 * - The backend service (AGG subscription) is feature flagged and is not currently live in production, hence calling this hook in production or without
 *   enabled flag will have no impact.
 *
 * @param installationId The installation id
 * @returns The subscribeClientEvent function that can be called to subscribe to client events
 */
export const useSubscribeRealtimeChannel = (installationId: string, context: string) => {
	const activeSubscriptions = useRef<Map<string, Subscription>>(new Map());

	useEffect(() => {
		const currentSubscriptions = activeSubscriptions.current;
		return () => {
			unsubscribeAllSubscriptions(currentSubscriptions);
		};
	}, []);

	// this is the bridge method that will be called from the extension
	return async (payload: SubscribeRealtimeChannelPayload, contextToken?: string) => {
		const { channelName, onEvent, options, isGlobal } = payload.data;

		const contextKey = getContextKey({ channelName: channelName, isGlobal });
		const existingSubscription = activeSubscriptions.current.get(contextKey);

		const { replay } = options || {};

		if (existingSubscription) {
			// if subscription already exists
			existingSubscription.addCallback(onEvent);
			onEvent('SUBSCRIPTION_STARTED');
		} else {
			// if no subscription exists, create a new one
			const subscription = subscribe(
				{
					operationName: 'realtimeChannel',
					query: print(realtimeChannel),
					variables: {
						installationId,
						name: channelName,
						context,
						isGlobal: isGlobal ?? false,
					},
					extensions: {
						'x-forge-context-token': contextToken,
						...(replay && {
							command: {
								type: 'replay',
								data: {
									// Timestamp in seconds of when to replay messages from. Valid for up to 7 days in the past.
									sequenceNumber: Date.now() / 1000 - replay,
								},
							},
						}),
					},
				},
				{
					next: (result: SubscriptionResult) => {
						const { payload } = result.data.ecosystem.realtimeChannel ?? {};
						subscription.onNext(payload);
					},
					error: (err) => {
						subscription.onError(err as Error);
					},
					complete: () => {},
				},
				onEvent,
			);

			// add new subscription to the subscription set
			activeSubscriptions.current.set(contextKey, subscription);
		}

		return {
			unsubscribe: () => {
				const subscription = activeSubscriptions.current.get(contextKey);
				if (subscription) {
					// remove one subscription
					subscription.removeCallback(onEvent);

					// if no more subscriptions remove the contextKey and unsubscribe
					if (subscription.subscriptionCallbacks.length === 0) {
						activeSubscriptions.current.delete(contextKey);
						if (!subscription.closed) {
							subscription.unsubscribe();
						}
					}
				}
			},
		};
	};
};
